import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import MarkdownContent from "../../components/markdown/MarkdownContent";
import BoardSnapshot from "../imageboard/BoardSnapshot";
import StatusBadge from "../../components/status-badge/StatusBadge";
import type {
  SharedNote,
  SharedNoteFolder,
  SharedNoteFolderContent,
} from "../../app/services/projectMessages";
import {
  MISSING_NOTE_HREF,
  NOTE_HREF_PREFIX,
  extractNoteIdFromHref,
  resolveWikilinks,
} from "../notes/utils/markdown";
import { formatDateTime } from "../talkroom/components/FileTable/fileTableUtils";

/**
 * 共有ノートのフォルダ共有リンク（/share/:token）の閲覧専用ビューア。
 *
 * 左に共有したフォルダ（子孫を含む）のツリー、右に選んだノートの本文を
 * 表示する。本文中の `[[wikilink]]` は共有範囲内のノートだけ解決し、
 * クリックでそのノートへ切り替える（範囲外はグレーの未解決表示）。
 */

const INDENT_STEP = 16;

const Layout = styled.div`
  width: 100%;
  max-width: 1080px;
  display: flex;
  align-items: flex-start;
  gap: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TreePane = styled.nav`
  width: 260px;
  flex-shrink: 0;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fff;
  padding: 8px 0;

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const TreeRow = styled.button<{ $depth: number; $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 28px;
  padding: 0 8px 0 ${({ $depth }) => 8 + $depth * INDENT_STEP}px;
  border: none;
  background: ${({ $selected }) =>
    $selected ? "var(--soremo-border)" : "transparent"};
  color: var(--soremo-gray);
  text-align: left;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};

  .material-symbols-rounded {
    font-size: 16px;
    flex-shrink: 0;
    color: var(--soremo-light-gray);
  }

  .tree-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (hover: hover) {
    &:not(:disabled):hover {
      background: ${({ $selected }) =>
        $selected ? "var(--soremo-border)" : "var(--soremo-background)"};
    }
  }
`;

const ContentPane = styled.article`
  flex: 1;
  min-width: 0;
  padding: 24px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fff;

  a[href^="${NOTE_HREF_PREFIX}"] {
    color: var(--soremo-blue);
    cursor: pointer;
  }
  a[href="${MISSING_NOTE_HREF}"] {
    color: var(--soremo-light-gray);
    border-bottom: 1px dashed var(--soremo-light-gray);
    cursor: default;
  }
`;

type TreeEntry =
  | { kind: "folder"; folder: SharedNoteFolder }
  | { kind: "note"; note: SharedNote };

/** コンテナ（null = 共有したフォルダ直下）ごとの行を sort_order 順に並べる */
const buildTree = (
  folders: SharedNoteFolder[],
  notes: SharedNote[],
): Map<string | null, TreeEntry[]> => {
  const byContainer = new Map<string | null, TreeEntry[]>();
  const push = (key: string | null, entry: TreeEntry) => {
    const list = byContainer.get(key) ?? [];
    list.push(entry);
    byContainer.set(key, list);
  };
  folders.forEach((folder) => push(folder.parent_id, { kind: "folder", folder }));
  notes.forEach((note) => push(note.folder_id, { kind: "note", note }));
  const order = (e: TreeEntry) =>
    e.kind === "folder" ? e.folder.sort_order : e.note.sort_order;
  byContainer.forEach((list) => list.sort((a, b) => order(a) - order(b)));
  return byContainer;
};

export interface NoteFolderShareViewProps {
  shared: SharedNoteFolderContent;
}

const NoteFolderShareView: React.FC<NoteFolderShareViewProps> = ({ shared }) => {
  const tree = useMemo(
    () => buildTree(shared.folders, shared.notes),
    [shared.folders, shared.notes],
  );

  // ツリーの表示順（深さ優先）。最初のノートを初期表示にする
  const rows = useMemo(() => {
    const result: { entry: TreeEntry; depth: number }[] = [];
    const walk = (key: string | null, depth: number) => {
      (tree.get(key) ?? []).forEach((entry) => {
        result.push({ entry, depth });
        if (entry.kind === "folder") walk(entry.folder.id, depth + 1);
      });
    };
    walk(null, 0);
    return result;
  }, [tree]);

  const firstNoteId = useMemo(() => {
    const first = rows.find((r) => r.entry.kind === "note");
    return first?.entry.kind === "note" ? first.entry.note.id : null;
  }, [rows]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected =
    shared.notes.find((n) => n.id === (selectedId ?? firstNoteId)) ?? null;

  // wikilink は共有範囲内のノートだけ解決する（範囲外は未解決表示）
  const wikilinkMap = useMemo(() => {
    const map: Record<string, string> = {};
    shared.notes.forEach((n) => {
      if (!(n.title in map)) map[n.title] = n.id;
    });
    return map;
  }, [shared.notes]);

  const handleContentClick = useCallback((e: React.MouseEvent) => {
    const href = (e.target as HTMLElement).closest("a")?.getAttribute("href");
    if (!href?.startsWith(NOTE_HREF_PREFIX)) return;
    e.preventDefault();
    const noteId = extractNoteIdFromHref(href);
    if (noteId) setSelectedId(noteId);
  }, []);

  return (
    <Layout>
      <TreePane aria-label={`${shared.name} のノート一覧`}>
        {rows.length === 0 && (
          <p className="bodytext-13 u-text-light-gray u-pl8">
            共有されているノートはありません
          </p>
        )}
        {rows.map(({ entry, depth }) =>
          entry.kind === "folder" ? (
            <TreeRow key={entry.folder.id} $depth={depth} disabled>
              <span className="material-symbols-rounded">folder</span>
              <span className="tree-label bodytext-13">
                {entry.folder.title}
              </span>
            </TreeRow>
          ) : (
            <TreeRow
              key={entry.note.id}
              $depth={depth}
              $selected={entry.note.id === selected?.id}
              onClick={() => setSelectedId(entry.note.id)}
            >
              <span className="material-symbols-rounded">
                {{ card: "style", board: "imagesmode", note: "description" }[
                  entry.note.display_style
                ]}
              </span>
              <span className="tree-label bodytext-13">{entry.note.title}</span>
            </TreeRow>
          ),
        )}
      </TreePane>

      <ContentPane>
        {selected ? (
          <div className="u-col u-gap16" style={{ alignItems: "stretch" }}>
            <div className="u-col u-gap8">
              <div className="u-row u-gap8 u-items-center">
                <h2 className="heading-spacing-21 u-line-height-150">
                  {selected.title}
                </h2>
                {selected.display_style === "card" && selected.category && (
                  <StatusBadge label={selected.category} size="small" />
                )}
              </div>
              {selected.modified && (
                <span className="bodytext-11 u-text-light-gray">
                  更新: {formatDateTime(selected.modified)}
                </span>
              )}
            </div>
            {selected.display_style === "board" ? (
              <BoardSnapshot
                key={selected.id}
                items={selected.board?.items ?? []}
                strokes={selected.board?.strokes ?? []}
              />
            ) : (
              <MarkdownContent
                content={resolveWikilinks(selected.content, wikilinkMap)}
                variant="document"
                breaks
                zoomableImages
                attachmentDisplay="preview-card"
                className="u-text-justify"
                onClick={handleContentClick}
              />
            )}
          </div>
        ) : (
          <p className="bodytext-13 u-text-light-gray">
            表示できるノートがありません
          </p>
        )}
      </ContentPane>
    </Layout>
  );
};

export default NoteFolderShareView;
